// custom code start
// home-hero slider — cross-fade stage + synced "next slide" peek, pagination,
// arrows, autoplay (pauses on hover/focus/hidden-tab/reduced-motion), keyboard.
if (!customElements.get('home-hero-slider')) {
  class HomeHeroSlider extends HTMLElement {
    constructor() {
      super();
      this.index = 0;
      this.autoplayTimer = null;
      this._onVisibility = this._onVisibility.bind(this);
    }

    connectedCallback() {
      this.count = parseInt(this.dataset.count, 10) || this.slides.length;

      // Enable transitions only after first paint so the LCP slide is not animated in.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.classList.add('is-ready'));
      });

      if (this.count < 2) return;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this._bindEvents();
      this._bindSwipe();
      this._setRovingTabindex();
      this._updateVideos();
      this._updateProgress();
      this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      document.removeEventListener('visibilitychange', this._onVisibility);
    }

    /* ---- element collections ---- */
    get slides() {
      return Array.from(this.querySelectorAll('.home-hero__slide'));
    }
    get peekLayers() {
      return Array.from(this.querySelectorAll('.home-hero__peek-layer'));
    }
    get bars() {
      return Array.from(this.querySelectorAll('.home-hero__bar'));
    }
    get progressFill() {
      return this.querySelector('.home-hero__progress-fill');
    }

    /* ---- events ---- */
    _bindEvents() {
      const prev = this.querySelector('.home-hero__arrow--prev');
      const next = this.querySelector('.home-hero__arrow--next');
      if (prev) prev.addEventListener('click', () => this.prev());
      if (next) next.addEventListener('click', () => this.next());

      this.bars.forEach((bar) => {
        bar.addEventListener('click', () => {
          this.goTo(parseInt(bar.dataset.index, 10));
        });
      });

      const pagination = this.querySelector('.home-hero__pagination');
      if (pagination) pagination.addEventListener('keydown', (e) => this._onKeydown(e));

      // Pause autoplay while the user is interacting.
      this.addEventListener('pointerenter', () => this.stopAutoplay());
      this.addEventListener('pointerleave', () => this.startAutoplay());
      this.addEventListener('focusin', () => this.stopAutoplay());
      this.addEventListener('focusout', () => this.startAutoplay());
      document.addEventListener('visibilitychange', this._onVisibility);
    }

    /* ---- swipe / drag (mouse + touch) ---- */
    _bindSwipe() {
      const area = this.querySelector('.home-hero__viewport') || this;
      const threshold = 40; // px
      this._swipe = { x: 0, y: 0, active: false };
      this._swiped = false;

      // Images are draggable by default; the native ghost-drag eats the gesture.
      area.addEventListener('dragstart', (e) => e.preventDefault());

      area.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        this._swipe.active = true;
        // custom code start — touch swipes fire no click, so a stale flag would eat the next tap
        this._swiped = false;
        // custom code end
        this._swipe.x = e.clientX;
        this._swipe.y = e.clientY;
        // custom code start — capturing a mouse press that starts on a link/button retargets
        // the click to the viewport, so the CTA never navigates on desktop
        if (e.target.closest('a, button')) return;
        // custom code end
        try {
          area.setPointerCapture(e.pointerId); // guarantee we still get pointerup
        } catch (err) {
          /* no-op */
        }
      });

      area.addEventListener('pointerup', (e) => {
        if (!this._swipe.active) return;
        this._swipe.active = false;
        const dx = e.clientX - this._swipe.x;
        const dy = e.clientY - this._swipe.y;
        if (Math.abs(dx) >= threshold && Math.abs(dx) > Math.abs(dy)) {
          this._swiped = true;
          if (dx < 0) {
            this.next();
          } else {
            this.prev();
          }
        }
      });

      area.addEventListener('pointercancel', () => {
        this._swipe.active = false;
      });

      // A swipe must not trigger the banner link's click.
      area.addEventListener(
        'click',
        (e) => {
          if (this._swiped) {
            e.preventDefault();
            e.stopPropagation();
            this._swiped = false;
          }
        },
        true
      );
    }

    _onVisibility() {
      if (document.hidden) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    }

    _onKeydown(e) {
      const keys = {
        ArrowLeft: () => this.prev(),
        ArrowRight: () => this.next(),
        Home: () => this.goTo(0),
        End: () => this.goTo(this.count - 1),
      };
      if (keys[e.key]) {
        e.preventDefault();
        keys[e.key]();
        const activeBar = this.bars[this.index];
        if (activeBar) activeBar.focus();
      }
    }

    /* ---- navigation ---- */
    next() {
      this.goTo((this.index + 1) % this.count);
    }

    prev() {
      this.goTo((this.index - 1 + this.count) % this.count);
    }

    goTo(target) {
      if (target === this.index || target < 0 || target >= this.count) return;
      const prevIndex = this.index;
      this.index = target;
      const nextIndex = (target + 1) % this.count;

      // --- Content "FLIP": the new slide's content eases in from where the
      // previous slide's content sat. offsetLeft/Top are layout positions (unaffected
      // by any in-flight transform), and every slide is inset:0 so they're directly
      // comparable. The travel is clipped by the viewport's overflow. ---
      let morphInner = null;
      let dx = 0;
      let dy = 0;
      if (!this.reducedMotion) {
        const fromInner = this.slides[prevIndex] && this.slides[prevIndex].querySelector('.home-hero__content-inner');
        const toInner = this.slides[target] && this.slides[target].querySelector('.home-hero__content-inner');
        if (fromInner && toInner) {
          dx = fromInner.offsetLeft - toInner.offsetLeft;
          dy = fromInner.offsetTop - toInner.offsetTop;
          morphInner = toInner;
        }
      }

      this.slides.forEach((slide, i) => {
        const active = i === target;
        slide.classList.toggle('is-active', active);
        if (active) {
          slide.removeAttribute('aria-hidden');
        } else {
          slide.setAttribute('aria-hidden', 'true');
        }
      });

      if (morphInner) this._playMorph(morphInner, dx, dy);

      // Peek always previews the upcoming slide.
      this.peekLayers.forEach((layer, i) => {
        layer.classList.toggle('is-active', i === nextIndex);
      });

      this.bars.forEach((bar, i) => {
        const active = i === target;
        bar.classList.toggle('is-active', active);
        bar.setAttribute('aria-selected', active ? 'true' : 'false');
        bar.setAttribute('tabindex', active ? '0' : '-1');
      });

      this._updateVideos();
      this._updateProgress();

      // Restart the autoplay countdown after a manual/auto change.
      if (this.autoplayTimer) this.startAutoplay();
    }

    /* FLIP play: feed the measured offset to CSS vars and add .is-morphing for one
       frame (invert — jumps there with no transition, per CSS). Removing the class
       lets the base `transition: transform` ease it back. Transition/easing/duration
       all live in the stylesheet; JS only supplies the delta. */
    _playMorph(el, dx, dy) {
      if (dx === 0 && dy === 0) return;
      el.style.setProperty('--hh-morph-x', `${dx}px`);
      el.style.setProperty('--hh-morph-y', `${dy}px`);
      el.classList.add('is-morphing');
      el.getBoundingClientRect(); // commit the inverted position
      requestAnimationFrame(() => el.classList.remove('is-morphing'));
    }

    /* Progress-bar pagination (used when there are many slides). */
    _updateProgress() {
      const fill = this.progressFill;
      if (fill) {
        fill.style.width = `${((this.index + 1) / this.count) * 100}%`;
      }
      const bar = this.querySelector('.home-hero__progress');
      if (bar) {
        bar.setAttribute('aria-valuenow', this.index + 1);
      }
    }

    /* Play the media in the active slide AND the active peek; pause the rest.
       Handles both native <video> and external-video <iframe> (YouTube/Vimeo),
       because a hidden iframe's autoplay is unreliable until we tell it to play. */
    _updateVideos() {
      this.querySelectorAll('video').forEach((video) => {
        const layer = video.closest('.home-hero__slide, .home-hero__peek-layer');
        if (layer && layer.classList.contains('is-active')) {
          const played = video.play();
          if (played && played.catch) played.catch(() => {});
        } else {
          try {
            video.pause();
          } catch (e) {
            /* no-op */
          }
        }
      });

      this.querySelectorAll('.home-hero__slide iframe, .home-hero__peek-layer iframe').forEach((iframe) => {
        const layer = iframe.closest('.home-hero__slide, .home-hero__peek-layer');
        this._controlIframe(iframe, !!(layer && layer.classList.contains('is-active')));
      });
    }

    /* Play/pause an embedded YouTube or Vimeo iframe via its postMessage API. */
    _controlIframe(iframe, play) {
      const win = iframe.contentWindow;
      if (!win) return;
      const src = iframe.getAttribute('src') || '';
      try {
        if (src.indexOf('youtube') !== -1) {
          win.postMessage(
            JSON.stringify({ event: 'command', func: play ? 'playVideo' : 'pauseVideo', args: [] }),
            '*'
          );
        } else if (src.indexOf('vimeo') !== -1) {
          win.postMessage(JSON.stringify({ method: play ? 'play' : 'pause' }), '*');
        }
      } catch (e) {
        /* cross-origin / not ready — autoplay params still cover the first paint */
      }
    }

    _setRovingTabindex() {
      this.bars.forEach((bar, i) => {
        bar.setAttribute('tabindex', i === this.index ? '0' : '-1');
      });
    }

    /* ---- autoplay ---- */
    startAutoplay() {
      this.stopAutoplay();
      if (this.reducedMotion) return;
      if (this.dataset.autoplay !== 'true') return;
      const interval = parseInt(this.dataset.autoplayInterval, 10);
      if (!interval || interval <= 0) return;
      this.autoplayTimer = window.setInterval(() => this.next(), interval);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }
  }

  customElements.define('home-hero-slider', HomeHeroSlider);
}

/* ---- Theme editor: jump to the selected slide ----
   For shopify:block:select, e.target is the block element itself (the slide, which
   carries block.shopify_attributes) — so the slider is an ANCESTOR (closest), not a
   descendant. Fall back to a descendant lookup in case Shopify targets the section. */
if (window.Shopify && window.Shopify.designMode) {
  const findSlider = (el) =>
    (el.closest && el.closest('home-hero-slider')) ||
    (el.querySelector && el.querySelector('home-hero-slider'));

  document.addEventListener('shopify:block:select', (e) => {
    const slider = findSlider(e.target);
    if (!slider) return;
    const blockId = e.detail && e.detail.blockId;
    let slide = blockId
      ? slider.querySelector(`.home-hero__slide[data-block-id="${blockId}"]`)
      : null;
    if (!slide && e.target.closest) slide = e.target.closest('.home-hero__slide');
    if (slide) {
      slider.stopAutoplay();
      slider.goTo(parseInt(slide.dataset.index, 10));
    }
  });

  document.addEventListener('shopify:block:deselect', (e) => {
    const slider = findSlider(e.target);
    if (slider) slider.startAutoplay();
  });
}
// custom code end
